export type SessionExpiredTarget = 'user' | 'admin';
export type SessionExpiredReason = 'session_expired' | 'session_timeout';

function loginPathFor(target: SessionExpiredTarget): string {
  return target === 'admin' ? '/admin/login' : '/login';
}

/** True when the current URL is an authenticated admin screen (not the admin login page). */
export function isAdminAppPath(pathname: string = window.location.pathname): boolean {
  return pathname.startsWith('/admin') && pathname !== '/admin/login';
}

/** True when an API URL is an admin-only endpoint. */
export function isAdminApiEndpoint(endpoint: string): boolean {
  const lower = endpoint.toLowerCase();
  return (
    lower.includes('/admin/') ||
    lower.includes('/api/admin/') ||
    lower.includes('/logmanagement/')
  );
}

/**
 * Pick admin vs learner login when a session expires.
 * Prefers the current admin route, then admin API endpoints, then a stored admin token.
 */
export function resolveSessionExpiredTarget(options?: {
  endpoint?: string;
  hadAdminToken?: boolean;
}): SessionExpiredTarget {
  if (isAdminAppPath()) {
    return 'admin';
  }

  const endpoint = options?.endpoint ?? '';
  if (endpoint && isAdminApiEndpoint(endpoint)) {
    return 'admin';
  }

  const hadAdminToken =
    options?.hadAdminToken ?? !!localStorage.getItem('adminToken');
  if (hadAdminToken) {
    return 'admin';
  }

  return 'user';
}

/** Build login URL for expired session — message is resolved on the login page via i18n, not embedded here. */
export function buildSessionExpiredLoginUrl(
  target: SessionExpiredTarget = 'user',
  reason: SessionExpiredReason = 'session_expired'
): string {
  const loginPath = loginPathFor(target);
  const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  const params = new URLSearchParams();
  params.set('reason', reason);
  if (currentPath && currentPath !== loginPath) {
    params.set('returnUrl', currentPath);
  }
  return `${loginPath}?${params.toString()}`;
}

/**
 * Redirect once to login.
 * Callers often set `__sessionRedirectInProgress` before this runs. That flag only
 * suppresses duplicate handlers — it must not skip the navigation itself, or the
 * page stays on the expired screen and looks frozen.
 */
export function redirectToSessionExpiredLogin(
  target: SessionExpiredTarget = 'user',
  reason: SessionExpiredReason = 'session_expired'
): void {
  if ((window as any).__intentionalLogoutInProgress) {
    return;
  }

  const loginPath = loginPathFor(target);
  if (window.location.pathname === loginPath) {
    return;
  }

  if ((window as any).__sessionRedirectStarted) {
    return;
  }

  (window as any).__sessionRedirectInProgress = true;
  (window as any).__sessionRedirectStarted = true;
  window.location.replace(buildSessionExpiredLoginUrl(target, reason));
}
